import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import "./salesByProducts.css";

const PRODUCT_SORT_FIELDS = {
  productName: "productName",
  quantitySold: "quantitySold",
  grossSales: "grossSales",
  netSalesIncTax: "netSalesIncTax",
  grossProfit: "grossProfit",
};

const PRODUCT_SORT_LABELS = {
  [PRODUCT_SORT_FIELDS.productName]: "Product Name",
  [PRODUCT_SORT_FIELDS.quantitySold]: "Quantity Sold",
  [PRODUCT_SORT_FIELDS.grossSales]: "Gross Sales",
  [PRODUCT_SORT_FIELDS.netSalesIncTax]: "Net Sales Inc. Tax",
  [PRODUCT_SORT_FIELDS.grossProfit]: "Gross Profit",
};

const SalesByProducts = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [viewMode, setViewMode] = useState("combinedVariants");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const salesFetchSeqRef = useRef(0);

  // Date range state - now handled by Redux

  // Reporting categories state
  const [reportingCategories, setReportingCategories] = useState([]);
  const [loadingReportingCategories, setLoadingReportingCategories] = useState(false);

  // Additional dropdown states
  const [locations, setLocations] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuGroups, setMenuGroups] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingMenuGroups, setLoadingMenuGroups] = useState(false);

  // Column selection and preferences
  const pageKey = "salesBreakdownByProduct";
  const defaultVisibleColumns = {
    productSKU: true,
    quantitySold: true,
    grossSales: true,
    discountsPromotions: false,
    netSalesIncTax: true,
    netSalesExcTax: false,
    taxAmount: true,
    costOfGoods: false,
    grossProfit: true,
    grossProfitMargin: false,
    transactionQty: true,
    refundQty: true,
    refundValue: false
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "productName", label: "Product Name" };
  const defaultPageSize = { value: "20", label: "20" };
  const validPageSizes = ["20", "50", "100"];
  
  // Default filter values
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultStaff = { value: "all", label: "All Staff" };
  const defaultDevice = { value: "all", label: "All Devices" };
  const defaultReportingCategory = { value: "all", label: "All Types" };
  const defaultCategory = { value: "all", label: "All Categories" };
  const defaultMenuGroup = { value: "all", label: "All Menu Groups" };
  
  // Default to "This Week" if no existing filter
  const defaultDateRange = [
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate(),
  ];
  
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  
  // Get filter values from Redux
  const location = preferences.location || defaultLocation;
  const staff = preferences.staff || defaultStaff;
  const device = preferences.device || defaultDevice;
  const reportingCategory = preferences.reportingCategory || defaultReportingCategory;
  const category = preferences.category || defaultCategory;
  const menuGroup = preferences.menuGroup || defaultMenuGroup;
  const dateRange = preferences.dateRange || defaultDateRange;
  const [startDate, endDate] = dateRange;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: PRODUCT_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Function to get theme color
  // const getThemeColor = () => {
  //   const colorMap = {
  //     'red': '#dc3545',
  //     'pink': '#e91e63',
  //     'orange': '#fd7e14',
  //     'yellow': '#ffc107',
  //     'lime': '#84cc16',
  //     'green': '#198754',
  //     'teal': '#20c997',
  //     'cyan': '#0dcaf0',
  //     'blue': '#0d6efd',
  //     'purple': '#6f42c1',
  //     'indigo': '#6610f2',
  //     'dark': '#212529'
  //   };
  //   return colorMap[appTheme] || '#20c997'; // Default to teal
  // };

  

  // Filter options - Will be populated from API
  const locationOptions = locations.length > 0 ? locations : [
    { value: "all", label: "All Locations" }
  ];

  const staffOptions = staffData.length > 0 ? staffData : [
    { value: "all", label: "All Staff" }
  ];

  const deviceOptions = devices.length > 0 ? devices : [
    { value: "all", label: "All Devices" }
  ];

  // Reporting categories options will be populated from API
  const reportingCategoryOptions = reportingCategories.length > 0 ? reportingCategories : [
    { value: "all", label: "All Types" }
  ];

  // Categories options will be populated from API
  const categoryOptions = categories.length > 0 ? categories : [
    { value: "all", label: "All Categories" }
  ];

  // Menu groups options will be populated from API
  const menuGroupOptions = menuGroups.length > 0 ? menuGroups : [
    { value: "all", label: "All Menu Groups" }
  ];

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
  };

  const clearFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    dispatch(setFilterField({ pageKey, field: "menuGroup", value: defaultMenuGroup }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    setSearch("");
  };

  const hasFiltersApplied = () =>
    location?.value !== "all" ||
    staff?.value !== "all" ||
    device?.value !== "all" ||
    reportingCategory?.value !== "all" ||
    category?.value !== "all" ||
    menuGroup?.value !== "all" ||
    Boolean(search?.trim());

  const applyFilters = useCallback(async () => {
    const fetchSeq = ++salesFetchSeqRef.current;
    setLoading(true);
    // Avoid showing previous view-mode rows while the new request is in flight
    setSalesData([]);
    try {
      // Set default dates if not provided. For single-day selection (endDate null), use same day for both.
      const defaultStartDate = startDate
        ? moment(startDate).startOf('day')
        : moment().subtract(30, 'days').startOf('day');
      const defaultEndDate = endDate
        ? moment(endDate).endOf('day')
        : (startDate ? moment(startDate).endOf('day') : moment().endOf('day'));

      const selectedMenuFilter =
        menuGroup?.value != null && menuGroup.value !== "all" && menuGroup.value !== ""
          ? String(menuGroup.menuID ?? menuGroup.value).trim()
          : null;

      const params = {
        CompID,
        startDate: moment(defaultStartDate).format('YYYY-MM-DD'),
        endDate: moment(defaultEndDate).format('YYYY-MM-DD'),
        locationFilter: location.value !== 'all' ? location.value : null,
        staffFilter: staff.value !== 'all' ? staff.value : null,
        deviceFilter:
          device?.value != null && device.value !== '' && device.value !== 'all'
            ? String(device.value).trim()
            : null,
        reportingCategoryFilter: reportingCategory.value !== 'all' ? reportingCategory.value : null,
        categoryFilter: category.value !== 'all' ? category.value : null,
        menuFilter: selectedMenuFilter,
        combineVariants: viewMode === "combinedVariants",
        searchTerm: search?.trim() || null,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
        pageNumber,
        pageSize: Number(pageSize.value),
      };

      const response = await axiosInstance.get(APIs.GET_SALES_BREAKDOWN_BY_PRODUCT, { params });

      if (fetchSeq !== salesFetchSeqRef.current) return;

      if (response.data.status) {
        const responseData = response.data.data;
        const salesDataRaw = responseData?.SalesData || responseData?.salesData || responseData || [];
        const totalCount = responseData?.TotalCount || responseData?.totalCount || 0;
        const apiTotalPages = responseData?.TotalPages || responseData?.totalPages || 0;
        
        // Normalize property names to camelCase (in case backend sends PascalCase)
        const salesData = Array.isArray(salesDataRaw) ? salesDataRaw.map(item => ({
          menuItemID: item.menuItemID || item.MenuItem_ID || item.menuItem_ID,
          productName: item.productName || item.ProductName || '',
          productSKU: item.productSKU || item.ProductSKU || '',
          productType: item.productType || item.ProductType || '',
          quantitySold: item.quantitySold || item.QuantitySold || 0,
          grossSales: item.grossSales || item.GrossSales || 0,
          discountsPromotions: item.discountsPromotions || item.DiscountsPromotions || 0,
          netSalesIncTax: item.netSalesIncTax || item.NetSalesIncTax || 0,
          netSalesExcTax: item.netSalesExcTax || item.NetSalesExcTax || 0,
          taxAmount: item.taxAmount || item.TaxAmount || 0,
          costOfGoods: item.costOfGoods || item.CostOfGoods || 0,
          grossProfit: item.grossProfit || item.GrossProfit || 0,
          grossProfitMargin: item.grossProfitMargin || item.GrossProfitMargin || 0,
          transactionQty: item.transactionQty || item.TransactionQty || 0,
          refundQty: item.refundQty || item.RefundQty || 0,
          refundValue: item.refundValue || item.RefundValue || 0,
          avgItemPrice: item.avgItemPrice || item.AvgItemPrice || 0,
          categoryName: item.categoryName || item.CategoryName || '',
          menuGroupName: item.menuGroupName || item.MenuGroupName || ''
        })) : [];

        setSalesData(salesData);
        setTotalRecords(totalCount || salesData.length);
        setTotalPages(
          apiTotalPages || Math.ceil((totalCount || salesData.length) / Number(pageSize.value)) || 1
        );
      } else {
        showErrorToast("Error loading sales data");
        setSalesData([]);
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (error) {
      if (fetchSeq !== salesFetchSeqRef.current) return;
      console.error("Error fetching sales data:", error);
      showErrorToast("Error loading sales data");
      setSalesData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      if (fetchSeq === salesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [
    CompID,
    startDate,
    endDate,
    location,
    staff,
    device,
    reportingCategory,
    category,
    menuGroup,
    search,
    sortBy,
    sortOrder,
    pageNumber,
    pageSize,
    viewMode,
  ]);

  // CSV Export function
  const exportToCSV = () => {
    const csvContent = [
      // Headers
      "Product Name,SKU,Quantity Sold,Gross Sales,Discounts & Promotions,Net Sales Inc. Tax,Net Sales Exc. Tax,Tax Amount,Cost of Goods,Gross Profit,Gross Profit Margin %,Transaction Qty,Refund Qty,Refund Value",
      // Data rows
      ...salesData.map(row =>
        `${row.productName},${row.productSKU},${row.quantitySold},${row.grossSales},${row.discountsPromotions},${row.netSalesIncTax},${row.netSalesExcTax},${row.taxAmount},${row.costOfGoods},${row.grossProfit},${row.grossProfitMargin},${row.transactionQty},${row.refundQty},${row.refundValue}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales-breakdown-by-time.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    showSuccessToast("CSV file exported successfully");
  };

  // Export to Word
  const exportToWord = () => {
    const tableData = salesData.map(row => [
      row.productName,
      row.productSKU,
      row.quantitySold,
      `${getCurrencySymbol(compCurrency)} ${row.grossSales.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.discountsPromotions.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesIncTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesExcTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.taxAmount.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.costOfGoods.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.grossProfit.toFixed(2)}`,
      `${row.grossProfitMargin.toFixed(2)}%`
    ]);

    const htmlContent = `
      <html>
        <head>
          <title>Sales by Products Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>Sales by Products Report</h1>
          <table>
            <thead>
              <tr>
                <th>HOUR COMMENCING</th>
                <th>TRANSACTION QTY</th>
                <th>REFUND QTY</th>
                <th>REFUND VALUE</th>
                <th>ITEM QTY</th>
                <th>SALES INC. TAX</th>
                <th>DISCOUNTS & PROMOTIONS</th>
                <th>AVERAGE TRANSACTION NET SALES</th>
                <th>NET SALES INC. TAX</th>
                <th>NET SALES EXC. TAX</th>
              </tr>
            </thead>
            <tbody>
              ${tableData.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "sales_breakdown_by_time.docx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccessToast("Word document exported successfully!");
  };

  // Export to Excel
  const exportToExcel = () => {
    const csvContent = [
      // Excel Header
      ["PRODUCT NAME", "SKU", "QTY SOLD", "GROSS SALES", "DISCOUNTS & PROMOTIONS", "NET SALES INC. TAX", "NET SALES EXC. TAX", "TAX AMOUNT", "COST OF GOODS", "GROSS PROFIT", "GROSS PROFIT MARGIN %"],
      // Excel Data
      ...salesData.map(row => [
        row.productName,
        row.productSKU,
        row.quantitySold,
        row.grossSales.toFixed(2),
        row.discountsPromotions.toFixed(2),
        row.netSalesIncTax.toFixed(2),
        row.netSalesExcTax.toFixed(2),
        row.taxAmount.toFixed(2),
        row.costOfGoods.toFixed(2),
        row.grossProfit.toFixed(2),
        row.grossProfitMargin.toFixed(2)
      ])
    ].map(row => row.join("\t")).join("\n");

    const blob = new Blob([csvContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "sales_breakdown_by_time.xls");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccessToast("Excel file exported successfully!");
  };

  // Print Report
  const printReport = () => {
    const printWindow = window.open('', '_blank');
    const tableData = salesData.map(row => [
      row.productName,
      row.productSKU,
      row.quantitySold,
      `${getCurrencySymbol(compCurrency)} ${row.grossSales.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.discountsPromotions.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesIncTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesExcTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.taxAmount.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.costOfGoods.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.grossProfit.toFixed(2)}`,
      `${row.grossProfitMargin.toFixed(2)}%`
    ]);

    printWindow.document.write(`
      <html>
        <head>
          <title>Sales by Products Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            h1 { color: #333; text-align: center; }
            @media print {
              body { margin: 0; }
              table { font-size: 12px; }
            }
          </style>
        </head>
        <body>
          <h1>Sales by Products Report</h1>
          <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>
                <th>HOUR COMMENCING</th>
                <th>TRANSACTION QTY</th>
                <th>REFUND QTY</th>
                <th>REFUND VALUE</th>
                <th>ITEM QTY</th>
                <th>SALES INC. TAX</th>
                <th>DISCOUNTS & PROMOTIONS</th>
                <th>AVERAGE TRANSACTION NET SALES</th>
                <th>NET SALES INC. TAX</th>
                <th>NET SALES EXC. TAX</th>
              </tr>
            </thead>
            <tbody>
              ${tableData.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();

    showSuccessToast("Report sent to printer!");
  };

  // Fetch reporting categories
  const fetchReportingCategories = async () => {
    try {
      setLoadingReportingCategories(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          CompID
        }
      });
      const categories = response.data.data || response.data || [];

      console.log(categories);
      // Transform categories to dropdown format
      const categoryOptions = [
        { value: "all", label: "All Types" },
        ...categories.map(category => ({
          value: category.rC_ID || category.id,
          label: category.rC_Name || category.name
        }))
      ];

      setReportingCategories(categoryOptions);
    } catch (error) {
      console.error('Error fetching reporting categories:', error);
      showErrorToast("Error loading types");
    } finally {
      setLoadingReportingCategories(false);
    }
  };

  // Fetch locations (company branches)
  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: {
          CompID
        }
      });
      const locationData = response.data.data || response.data || [];
      
      // Transform locations to dropdown format
      const locationOptions = [
        { value: "all", label: "All Locations" },
        ...locationData.map(location => ({
          value: location.CompID,
          label: location.CompName
        }))
      ];
      
      setLocations(locationOptions);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showErrorToast("Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  };

  // Fetch staff
  const fetchStaff = async () => {
    try {
      setLoadingStaff(true);
      const response = await axiosInstance.get(APIs.GET_ALL_STAFF, {
        params: {
          CompID
        }
      });
      const staffData = response.data.data || response.data || [];

      // Transform staff to dropdown format
      const staffOptions = [
        { value: "all", label: "All Staff" },
        ...staffData.map(staffMember => ({
          value: staffMember.userName,
          label: `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim()  
        }))
      ];

      setStaffData(staffOptions);
    } catch (error) {
      console.error('Error fetching staff:', error);
      showErrorToast("Error loading staff");
    } finally {
      setLoadingStaff(false);
    }
  };

  // Fetch devices
  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEVICES, {
        params: {
          CompID
        }
      });
      const deviceData = response.data.data || response.data || [];

      // Transform devices to dropdown format
      const deviceOptions = [
        { value: "all", label: "All Devices" },
        ...deviceData.map((d) => {
          const id = d.deviceID ?? d.DeviceID;
          const name = d.deviceName ?? d.DeviceName ?? d.Name ?? "";
          return { value: id != null && id !== "" ? String(id).trim() : "", label: name || String(id ?? "") };
        }).filter((opt) => opt.value !== "")
      ];

      setDevices(deviceOptions);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showErrorToast("Error loading devices");
    } finally {
      setLoadingDevices(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: ""
        }
      });
      
      const categoryData = response.data.data || response.data || [];

      console.log('Categories Response:', categoryData); // Debug log

      // Transform categories to dropdown format
      const categoryOptions = [
        { value: "all", label: "All Categories" },
        ...(Array.isArray(categoryData) ? categoryData.map(cat => ({
          value: cat.rootCat_ID || cat.RootCat_ID || cat.id,
          label: cat.rootCat_Name || cat.RootCat_Name || cat.rC_Name || cat.name || 'Unknown Category'
        })) : [])
      ];

      setCategories(categoryOptions);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showErrorToast("Error loading categories");
      setCategories([{ value: "all", label: "All Categories" }]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch menu groups
  const fetchMenuGroups = async () => {
    try {
      setLoadingMenuGroups(true);
      const response = await axiosInstance.get(APIs.GET_All_MENU_GroupS, {
        params: {
          CompID
        }
      });
      const menuGroupData = response.data.data || response.data || [];

      // Transform menu groups to dropdown format
      const menuGroupOptions = [
        { value: "all", label: "All Menu Groups", menuID: "all" },
        ...menuGroupData.flatMap((menu) => {
          const menuID = menu.menu_ID ?? menu.Menu_ID ?? menu.id;
          const menuName = menu.menu_Name ?? menu.Menu_Name ?? menu.name ?? "Menu";
          const groups = Array.isArray(menu.menuGroups) ? menu.menuGroups : [];

          return groups
            .map((group) => ({
              // Backend `menuFilter` expects MenuID, not MenuGroupID
              value: menuID != null ? String(menuID).trim() : "",
              menuID: menuID != null ? String(menuID).trim() : "",
              menuGroupID: group.menuGroup_ID ?? group.MenuGroup_ID ?? null,
              label: `${menuName} - ${group.menuGroup_Name ?? group.MenuGroup_Name ?? "Group"}`,
            }))
            .filter((opt) => opt.value !== "");
        }),
      ];

      setMenuGroups(menuGroupOptions);
    } catch (error) {
      console.error('Error fetching menu groups:', error);
      showErrorToast("Error loading menu groups");
    } finally {
      setLoadingMenuGroups(false);
    }
  };

  // Handle date range changes
  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: [newStartDate, newEndDate],
        })
      );
    } else {
      showSuccessToast("Invalid date selected");
    }
  };

  // Initialize preferences
  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    
    // Initialize filter fields
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    if (!preferences.staff) {
      dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    }
    if (!preferences.device) {
      dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    }
    if (!preferences.reportingCategory) {
      dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    }
    if (!preferences.category) {
      dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    }
    if (!preferences.menuGroup) {
      dispatch(setFilterField({ pageKey, field: "menuGroup", value: defaultMenuGroup }));
    }
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    location?.value,
    staff?.value,
    device?.value,
    reportingCategory?.value,
    category?.value,
    menuGroup?.value,
    startDate,
    endDate,
    search,
    viewMode,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => applyFilters(), [applyFilters]);

  // Fetch all dropdown data on component mount
  useEffect(() => {
    fetchReportingCategories();
    fetchLocations();
    fetchStaff();
    fetchDevices();
    fetchCategories();
    fetchMenuGroups();
  }, []);

  const renderNoDataMessage = () => {
    if (loading || salesData.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No sales data found"
        description="Try adjusting your filters or date range."
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div className="sales-by-products-page">
      {/* Breadcrumb */}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Sales by Products</li>
          </ol>
          <h1 className="page-header mb-0">Sales by Products</h1>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {initialLoadComplete && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Sales by Products"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, type, category..."
            bodyFlush
            topFilters={
              <>
                <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                  <DateRangePicker
                    startDate={startDate && moment(startDate).isValid() ? startDate : null}
                    endDate={endDate && moment(endDate).isValid() ? endDate : null}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-map-marker">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange("location", option)}
                    options={locationOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="All Locations"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-user">
                  <Select
                    value={staff}
                    onChange={(option) => handleFilterChange("staff", option)}
                    options={staffOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingStaff}
                    placeholder="All Staff"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-desktop">
                  <Select
                    value={device}
                    onChange={(option) => handleFilterChange("device", option)}
                    options={deviceOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingDevices}
                    placeholder="All Devices"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-list">
                  <Select
                    value={reportingCategory}
                    onChange={(option) => handleFilterChange("reportingCategory", option)}
                    options={reportingCategoryOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingReportingCategories}
                    placeholder="All Types"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-tags">
                  <Select
                    value={category}
                    onChange={(option) => handleFilterChange("category", option)}
                    options={categoryOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingCategories}
                    placeholder="All Categories"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-bars">
                  <Select
                    value={menuGroup}
                    onChange={(option) => handleFilterChange("menuGroup", option)}
                    options={menuGroupOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingMenuGroups}
                    placeholder="All Menu Groups"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
              </>
            }
            headerControls={
              <ColumnSelection
                pageKey={pageKey}
                visibleColumns={visibleColumns}
                defaultVisibleColumns={defaultVisibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
            titleAddon={
              <div className="sales-products-variant-switch">
                <ToggleSwitch
                  name="variantWise"
                  size="small"
                  isChecked={viewMode === "variantWise"}
                  onToggle={(checked) => {
                    setSalesData([]);
                    setViewMode(checked ? "variantWise" : "combinedVariants");
                  }}
                  label="Variant-wise"
                />
              </div>
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {salesData.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <SortableColumnHeader
                            sortField={PRODUCT_SORT_FIELDS.productName}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            PRODUCT NAME
                          </SortableColumnHeader>
                          {visibleColumns.productSKU && <th className="pt-2 pb-2">SKU</th>}
                          {visibleColumns.quantitySold && (
                            <SortableColumnHeader
                              sortField={PRODUCT_SORT_FIELDS.quantitySold}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              QTY SOLD
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.grossSales && (
                            <SortableColumnHeader
                              sortField={PRODUCT_SORT_FIELDS.grossSales}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              GROSS SALES
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.discountsPromotions && (
                            <th className="pt-2 pb-2">DISCOUNTS &amp; PROMOTIONS</th>
                          )}
                          {visibleColumns.netSalesIncTax && (
                            <SortableColumnHeader
                              sortField={PRODUCT_SORT_FIELDS.netSalesIncTax}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              NET SALES INC. TAX
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.netSalesExcTax && (
                            <th className="pt-2 pb-2">NET SALES EXC. TAX</th>
                          )}
                          {visibleColumns.taxAmount && <th className="pt-2 pb-2">TAX AMOUNT</th>}
                          {visibleColumns.costOfGoods && <th className="pt-2 pb-2">COST OF GOODS</th>}
                          {visibleColumns.grossProfit && (
                            <SortableColumnHeader
                              sortField={PRODUCT_SORT_FIELDS.grossProfit}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              GROSS PROFIT
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.grossProfitMargin && (
                            <th className="pt-2 pb-2">GROSS PROFIT MARGIN %</th>
                          )}
                          {visibleColumns.transactionQty && (
                            <th className="pt-2 pb-2">TRANSACTION QTY</th>
                          )}
                          {visibleColumns.refundQty && <th className="pt-2 pb-2">REFUND QTY</th>}
                          {visibleColumns.refundValue && <th className="pt-2 pb-2">REFUND VALUE</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.map((row, index) => (
                          <tr
                            key={`${row.menuItemID ?? "x"}-${row.productName ?? ""}-${index}`}
                          >
                            <td>{row.productName}</td>
                            {visibleColumns.productSKU && <td>{row.productSKU}</td>}
                            {visibleColumns.quantitySold && <td>{row.quantitySold}</td>}
                            {visibleColumns.grossSales && (
                              <td style={{ color: "#0d6efd" }}>
                                {getCurrencySymbol(compCurrency)} {row.grossSales.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.discountsPromotions && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.discountsPromotions.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.netSalesIncTax && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.netSalesIncTax.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.netSalesExcTax && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.netSalesExcTax.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.taxAmount && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.taxAmount.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.costOfGoods && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.costOfGoods.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.grossProfit && (
                              <td style={{ color: row.grossProfit >= 0 ? "#198754" : "#dc3545" }}>
                                {getCurrencySymbol(compCurrency)} {row.grossProfit.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.grossProfitMargin && (
                              <td style={{ color: row.grossProfitMargin >= 0 ? "#198754" : "#dc3545" }}>
                                {row.grossProfitMargin.toFixed(2)}%
                              </td>
                            )}
                            {visibleColumns.transactionQty && <td>{row.transactionQty}</td>}
                            {visibleColumns.refundQty && <td>{row.refundQty}</td>}
                            {visibleColumns.refundValue && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.refundValue.toFixed(2)}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

    </div>
  );
};

export default SalesByProducts;

